// directive-redirect-ticket-commit.ts
//
// Re-review 2026-09-26 (area B, F-B1): commits the ticket files a redirect
// just wrote. F2 (`applyRedirectDirective`, proposed_changes) used to stop at
// `writeTicketFile`; the next dispatched tick's recoverOwnedCheckout
// (`git clean -fd`, supervisor-pre-dispatch-checks.ts) then erased the
// untracked file while the journal already said "applied". Same contract F3
// (redirect-text-to-decision-ticket.ts) follows: commit in the SAME tick,
// through commit-owner (the only sanctioned commit path), and surface a
// policy denial as a rejection reason instead of leaving a doomed file.
import { randomUUID } from "node:crypto";
import { join } from "node:path";
import { commitTicket, type GitRunner } from "./commit-owner.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { ticketFileName } from "./ticket-file-name.js";

export interface CommitRedirectTicketFilesInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  missionId: string;
  directiveId: string;
  /** Tickets whose files were just (re)written by the redirect — may be empty. */
  tickets: Ticket[];
  /** Stale sibling files a title rename removed (writeTicketFile's `removed`) — staged so the commit carries the deletion. */
  removedPaths: string[];
}

export type CommitRedirectTicketFilesResult = { ok: true } | { ok: false; reason: string };

/**
 * `git add -- <path>` rejects a pathspec that matches nothing, and a stale
 * sibling that was never committed (no bootstrap commit yet) is simply gone
 * once removed — only TRACKED siblings need their deletion staged.
 */
async function trackedSubset(
  gitRunner: GitRunner,
  repoDir: string,
  paths: string[],
): Promise<string[]> {
  if (paths.length === 0) return [];
  const res = await gitRunner(["ls-files", "--", ...paths], repoDir);
  if (res.status !== 0) return [];
  return res.stdout
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** No files written (supersede/gate-only redirect) ⇒ no commit, never an empty one. */
export async function commitRedirectTicketFiles(
  input: CommitRedirectTicketFilesInput,
): Promise<CommitRedirectTicketFilesResult> {
  if (input.tickets.length === 0) return { ok: true };
  const ids = input.tickets.map((t) => t.id);
  const removedTracked = await trackedSubset(input.gitRunner, input.repoDir, input.removedPaths);
  const commit = await commitTicket({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: [
      ...input.tickets.map((t) => join(".avatar", "tickets", ticketFileName(t))),
      ...removedTracked,
    ],
    missionId: input.missionId,
    runId: randomUUID(),
    ticketId: ids[0] as string,
    type: "chore",
    scope: "autopilot",
    subject: `redirect ${input.directiveId} -> ${ids.join(", ")}`,
  });
  if (commit.status === "policy_denied") {
    return { ok: false, reason: `commit ticket bị policy từ chối: ${commit.reason}` };
  }
  return { ok: true };
}
